import type { ColDef } from "ag-grid-community"
import { COLUMN_VISIBILITY, type ColumnVisibility } from "@/store/columnVisibility"

export type ColumnVisibilityPanelEntry = {
  key: string
  label: string
  visible: boolean
  alwaysVisible: boolean
  defaultVisible: boolean
}

export type KTableColumnDefinition = ColDef<any> & {
  alwaysVisible?: boolean
  columnVisibilityKey?: string
  columnVisibilityLabel?: string
  hideFromColumnVisibility?: boolean
  onlyForExport?: boolean
  excelHeaderName?: string
  hideInDownload?: boolean
  includeInDownloadWhenHidden?: boolean
}

export type KTableColumnVisibilityMode = "user_preferences" | "reporting" | "disabled"
export type ReportingColumnConfig = Record<string, ColumnVisibility>

export type ColumnDefinitionWithVisibilityKey = KTableColumnDefinition & {
  resolvedVisibilityKey: string | null
  resolvedVisibilityLabel: string
}

export type ToggleableColumn = ColumnDefinitionWithVisibilityKey & {
  resolvedVisibilityKey: string
}

export function getVisibleColumnKeysFromReportingConfig(reportingColumnConfig: ReportingColumnConfig): string[]
export function getVisibleColumnKeysFromReportingConfig(reportingColumnConfig: null): null
export function getVisibleColumnKeysFromReportingConfig(reportingColumnConfig: ReportingColumnConfig | null): string[] | null
export function getVisibleColumnKeysFromReportingConfig(reportingColumnConfig: ReportingColumnConfig | null): string[] | null {
  if (reportingColumnConfig === null) {
    return null
  }

  return Object.entries(reportingColumnConfig)
    .filter(([, visibility]) => visibility !== COLUMN_VISIBILITY.HIDDEN)
    .map(([columnKey]) => columnKey)
}

export function columnConfigFromVisibleKeys(baseColumns: ReportingColumnConfig, visibleColumnKeys: string[], managedColumnKeys: string[]): ReportingColumnConfig {
  const selectedColumnKeys = new Set(visibleColumnKeys)
  const updatedColumns = { ...baseColumns }

  managedColumnKeys.forEach((columnKey) => {
    if (updatedColumns[columnKey] !== COLUMN_VISIBILITY.ALWAYS_VISIBLE) {
      updatedColumns[columnKey] = selectedColumnKeys.has(columnKey) ? COLUMN_VISIBILITY.VISIBLE : COLUMN_VISIBILITY.HIDDEN
    }
  })

  return updatedColumns
}

export function normalizeReportingColumnConfig(defaultColumns: ReportingColumnConfig, reportingColumnConfig: ReportingColumnConfig): ReportingColumnConfig {
  return Object.fromEntries(
    Object.entries(defaultColumns).map(([columnKey, defaultVisibility]) => {
      if (defaultVisibility === COLUMN_VISIBILITY.ALWAYS_VISIBLE) {
        return [columnKey, COLUMN_VISIBILITY.ALWAYS_VISIBLE]
      }

      return [columnKey, reportingColumnConfig[columnKey] ?? COLUMN_VISIBILITY.HIDDEN]
    }),
  )
}

export function hasSameColumnConfig(firstColumns: ReportingColumnConfig | null, secondColumns: ReportingColumnConfig): boolean {
  if (firstColumns === null) {
    return false
  }

  const secondColumnKeys = Object.keys(secondColumns)

  return Object.keys(firstColumns).length === secondColumnKeys.length && secondColumnKeys.every((columnKey) => firstColumns[columnKey] === secondColumns[columnKey])
}

export function getColumnVisibilityKey(column: KTableColumnDefinition): string | null {
  if (column.columnVisibilityKey) {
    return column.columnVisibilityKey
  }
  if (column.field) {
    return column.field
  }

  return null
}

export function getColumnVisibilityLabel(column: KTableColumnDefinition): string {
  return column.columnVisibilityLabel ?? column.headerName ?? getColumnVisibilityKey(column) ?? ""
}

export function isToggleableColumn(column: ColumnDefinitionWithVisibilityKey): column is ToggleableColumn {
  return !column.hideFromColumnVisibility && !!column.resolvedVisibilityKey && !column.onlyForExport
}

export function buildPanelEntry(column: ToggleableColumn, resolvedColumns: ReportingColumnConfig, defaultColumns: ReportingColumnConfig): ColumnVisibilityPanelEntry {
  const columnKey = column.resolvedVisibilityKey
  const alwaysVisible = resolvedColumns[columnKey] === COLUMN_VISIBILITY.ALWAYS_VISIBLE || !!column.alwaysVisible

  return {
    key: columnKey,
    label: column.resolvedVisibilityLabel,
    visible: alwaysVisible || resolvedColumns[columnKey] !== COLUMN_VISIBILITY.HIDDEN,
    alwaysVisible,
    defaultVisible: defaultColumns[columnKey] !== COLUMN_VISIBILITY.HIDDEN,
  }
}
